import type { ComponentProps, ReactNode } from 'react';
import React, { useEffect } from 'react';

import { LocalHistoryEditor } from '@aline/common';
import { Typography } from '@mui/material';
import type { FallbackRender } from '@sentry/react';
import { ErrorBoundary } from '@sentry/react';
import { useIntercom } from 'react-use-intercom';
import type { Editor } from 'slate';
import { AlineButton } from 'src/components';
import { AlineErrorIcon } from 'src/components/icons/AlineErrorIcon';
import { useEditingDisabled } from 'src/store/document/selectors';
import { themeV5 } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

type PotentialFix = 'reset' | 'undo' | 'reload';

/**
 * The set of things the user has already tried to resolve the error.
 */
const triedFixes = new Set<PotentialFix>();

/**
 * Use session storage to detect if the user is refreshing the page in order to fix an error. When
 * the page loads again, we add 'reload' to triedFixes so that we can reflect this in the UI.
 */
const TRIED_RELOADING_KEY = 'editor-error-boundary-tried-reloading';

if (window.sessionStorage.getItem(TRIED_RELOADING_KEY)) {
  triedFixes.add('reload');
  window.sessionStorage.removeItem(TRIED_RELOADING_KEY);
}

export interface EditorErrorBoundaryProps {
  children: ReactNode;
  editor: Editor;
}

export function EditorErrorBoundary({ children, editor }: { children: ReactNode; editor: Editor }) {
  return (
    <ErrorBoundary fallback={props => <Fallback editor={editor} {...props} />}>
      {children}
      <ResetTriedFixes />
    </ErrorBoundary>
  );
}

interface FallbackProps extends ComponentProps<FallbackRender> {
  editor: Editor;
}

const useStyles = makeStyles()(theme => ({
  content: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    margin: 'auto',
    maxWidth: 520,
    textAlign: 'center',
  },

  iconCircle: {
    backgroundColor: 'white',
    border: '1px solid',
    borderColor: theme.palette.grey[200],
    borderRadius: '50%',
    padding: 16,
  },

  root: {
    background: theme.appColors.defaultFieldBackground,
    display: 'flex',
    inset: 0,
    padding: theme.spacing(1),
    position: 'absolute',
  },
}));

function Fallback({ editor, resetError }: FallbackProps) {
  const { show: showIntercom } = useIntercom();
  const editingDisabled = useEditingDisabled();
  const { classes } = useStyles();

  const canUndo =
    !editingDisabled &&
    LocalHistoryEditor.isYHistoryEditor(editor) &&
    LocalHistoryEditor.canUndo(editor);

  const triedReloading = triedFixes.has('reload');
  const triedResetting = triedReloading || triedFixes.has('reset');
  const triedUndoing = canUndo && (triedReloading || triedFixes.has('undo'));

  function handleReset() {
    triedFixes.add('reset');
    resetError();
  }

  function handleUndo() {
    if (canUndo) editor.undo();
    triedFixes.add('undo');
    resetError();
  }

  function handleReload() {
    window.sessionStorage.setItem(TRIED_RELOADING_KEY, 'true');
    window.location.reload();
  }

  function getStrategy(): { label: string; onClick: () => void } {
    if (!triedResetting)
      return {
        label: 'Try again',
        onClick: handleReset,
      };

    if (canUndo && !triedUndoing)
      return {
        label: 'Undo last change',
        onClick: handleUndo,
      };

    if (!triedReloading)
      return {
        label: 'Refresh page',
        onClick: handleReload,
      };

    return {
      label: 'Contact support',
      onClick: showIntercom,
    };
  }

  const { label, onClick } = getStrategy();

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.iconCircle}>
          <AlineErrorIcon fill={themeV5.palette.error.light} height={48} width={48} />
        </div>

        <Typography variant="h1">An error occurred</Typography>
        <Typography>Something went wrong with your document.</Typography>

        <AlineButton color="primary" variant="outlined" onClick={onClick}>
          {label}
        </AlineButton>
      </div>
    </div>
  );
}

/**
 * If this component is rendered successfully and remain so, this indicates that the error has been
 * resolved, so we can clear the set of tried fixes.
 */
function ResetTriedFixes() {
  useEffect(() => {
    const timer = setTimeout(() => triedFixes.clear(), 5000);
    return () => clearTimeout(timer);
  }, []);

  return null;
}
